import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { ReturnCommentHistory } from './ReturnCommentHistory';
import { GoalReferencePanel, type EvaluationGoalReference } from './GoalReferencePanel';
import { ItemGoalReference, hasAnyItemGoal } from './ItemGoalReference';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import { formatDateJST } from '../../utils/dateUtils';
import { getStatusLabel, canViewSalaryRate, type EvaluationStatus } from '../../utils/statusLabels';
import { useAuth } from '../../contexts/AuthContext';

interface Employee {
  employee_id?: string;
  employee_name?: string;
  name?: string;
  email?: string;
  hire_date?: string;
  department_id?: string;
  department_name?: string;
  department_type?: string;
  job_grade?: string;
  job_title?: string;
  rank?: string;
  rank_cost?: number;
  monthly_cost?: number;
}

interface Evaluation {
  evaluation_id?: string;
  employee_id?: string;
  evaluation_period?: string;
  period_name?: string;
  department_type?: string;
  status?: string;
  performance_score?: number | null;
  salary_increase_rate?: number | null;
  annual_sales?: number | null;
  current_rank_cost?: number | null;
  team_member_count?: number | null;
  domestic_member_count?: number | null;
  offshore_member_count?: number | null;
  quantitative_dp_value?: number | null;
  quantitative_dp_score?: number | null;
  hr_approved_by?: string | null;
  hr_approved_at?: string | null;
  hr_comment?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface EvaluationItem {
  item_detail_id?: string;
  item_id?: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  target_description?: string;
  achievement_criteria?: string | null;
  first_evaluation_score?: number | null;
  first_evaluation_comment?: string | null;
  second_evaluation_score?: number | null;
  second_evaluation_comment?: string | null;
  third_evaluation_score?: number | null;
  third_evaluation_comment?: string | null;
  final_evaluation_score?: number | null;
}

interface SalesMetrics {
  target_expected_order?: number;
  target_sales?: number;
  target_gross_profit?: number;
  actual_expected_order?: number;
  actual_sales?: number;
  actual_gross_profit?: number;
}

interface Evaluator {
  evaluation_category: string;
  evaluator_id: string;
  evaluator_name?: string;
  evaluation_weight: number;
}

interface EvaluatorScore {
  evaluation_category: string;
  evaluator_id: string;
  evaluator_name: string;
  evaluation_weight: number;
  individual_score: number;
  submitted_at?: string;
  is_completed?: boolean;
}

interface EvaluatorItemScore {
  evaluator_id: string;
  evaluation_category: string;
  item_id: string;
  evaluation_score: number;
  evaluation_comment?: string;
  evaluator_name: string;
  evaluation_weight: number;
}

interface EvaluationDetailViewProps {
  employee: Employee | null;
  evaluation: Evaluation | null;
  evaluationItems?: EvaluationItem[];
  salesMetrics?: SalesMetrics | null;
  evaluators?: Evaluator[];
  evaluatorScores?: EvaluatorScore[];
  evaluatorItemScores?: EvaluatorItemScore[];
  goal?: EvaluationGoalReference | null;
  showApprovalSection?: boolean;
}

const EvaluationDetailView: React.FC<EvaluationDetailViewProps> = ({
  employee,
  evaluation,
  evaluationItems = [],
  salesMetrics = null,
  evaluators = [],
  evaluatorScores = [],
  evaluatorItemScores = [],
  goal = null,
  showApprovalSection = false,
}) => {
  const { user } = useAuth();

  const getCategoryLabel = (category: string) => {
    const categoryMap: Record<string, string> = {
      'self': '自己評価',
      'manager': '上長評価',
      'dept_head': '部門長評価',
    };
    return categoryMap[category] || category;
  };

  const formatNumber = (value: any, decimals: number = 2): string => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '−';
    }
    return Number(value).toFixed(decimals);
  };

  const calculateAchievementRate = (actual: number | null | undefined, target: number | null | undefined): string => {
    if (!actual || !target || target === 0) {
      return '−';
    }
    return ((actual / target) * 100).toFixed(2) + '%';
  };

  if (!employee) {
    return (
      <Typography color="text.secondary">
        データが見つかりません
      </Typography>
    );
  }

  const status = getStatusLabel((evaluation?.status ?? null) as EvaluationStatus);

  // カテゴリ別にグループ化
  const groupedItems = evaluationItems.reduce((acc, item) => {
    if (!acc[item.category_number]) {
      acc[item.category_number] = {
        category_name: item.category_name,
        items: []
      };
    }
    acc[item.category_number].items.push(item);
    return acc;
  }, {} as Record<number, { category_name: string; items: EvaluationItem[] }>);

  // 各カテゴリ内の項目を項目番号順にソート
  Object.keys(groupedItems).forEach(categoryNum => {
    groupedItems[Number(categoryNum)].items.sort((a, b) => {
      // item_numberから数値部分を抽出してソート (例: "B-10" -> 10)
      const aNum = parseInt(a.item_number.replace(/[^\d]/g, ''));
      const bNum = parseInt(b.item_number.replace(/[^\d]/g, ''));
      return aNum - bNum;
    });
  });

  const employeeName = employee.employee_name || employee.name || '−';
  const employeeDeptType = employee.department_type || evaluation?.department_type;

  // 評価カテゴリ別の評価者を取得（評価ルートから）
  const getEvaluatorsByCategory = (category: string): Evaluator[] => {
    return evaluators.filter(ev => ev.evaluation_category === category);
  };

  const managerEvaluators = getEvaluatorsByCategory('manager');
  const deptHeadEvaluators = getEvaluatorsByCategory('dept_head');

  // 特定の評価者の項目別スコアを取得
  const getEvaluatorScore = (evaluatorId: string, itemId: string): string => {
    const itemScore = evaluatorItemScores.find(
      eis => eis.evaluator_id === evaluatorId && eis.item_id === itemId
    );
    if (itemScore && itemScore.evaluation_score !== null && itemScore.evaluation_score !== undefined) {
      return formatNumber(itemScore.evaluation_score, 0);
    }
    return '−';
  };

  return (
    <Box>
      {/* 従業員基本情報 */}
      <Card sx={{ mb: 3 }} variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            従業員情報
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">社員ID</Typography>
              <Typography variant="body1">{employee.employee_id || '−'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">氏名</Typography>
              <Typography variant="body1">{employeeName}</Typography>
            </Grid>
            {employee.email && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">メールアドレス</Typography>
                <Typography variant="body1">{employee.email}</Typography>
              </Grid>
            )}
            {employee.hire_date && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">入社日</Typography>
                <Typography variant="body1">{formatDateJST(employee.hire_date)}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">部門</Typography>
              <Typography variant="body1">{employee.department_name || '−'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">職級</Typography>
              <Typography variant="body1">{employee.job_grade || '−'}</Typography>
            </Grid>
            {employee.job_title && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">役職</Typography>
                <Typography variant="body1">{employee.job_title}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">ランク</Typography>
              <Typography variant="body1">{employee.rank || '−'}</Typography>
            </Grid>
            {employee.rank_cost !== null && employee.rank_cost !== undefined && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">年額ランクコスト</Typography>
                <Typography variant="body1">{formatCurrencyWithUnit(employee.rank_cost)}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* 評価情報 */}
      {evaluation ? (
        <>
          <Card sx={{ mb: 3 }} variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                評価結果
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">評価期間</Typography>
                  <Typography variant="body1">{evaluation.period_name || evaluation.evaluation_period || '−'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">ステータス</Typography>
                  <Chip label={status.label} color={status.color} size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">パフォーマンス評価点（P）</Typography>
                  <Typography variant="h5" color="primary">
                    {formatNumber(evaluation.performance_score)}点
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">昇給率</Typography>
                  {canViewSalaryRate(
                    evaluation?.status as EvaluationStatus,
                    user?.role || 'employee',
                    true
                  ) ? (
                    <Typography variant="h5" color="secondary">
                      {formatNumber(evaluation.salary_increase_rate)}%
                    </Typography>
                  ) : (
                    <Tooltip title="昇給率は評価確定後に表示されます">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityOffIcon sx={{ color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.disabled">
                          非表示
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* 差し戻し履歴 */}
          {evaluation.evaluation_id && (
            <Card sx={{ mb: 3 }} variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  差し戻し履歴
                </Typography>
                <ReturnCommentHistory
                  evaluationId={evaluation.evaluation_id}
                  showAll={true}
                />
              </CardContent>
            </Card>
          )}

          {/* 評価者情報 */}
          {evaluators.length > 0 && (
            <Card sx={{ mb: 3 }} variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  評価者情報
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '150px' }}>評価区分</TableCell>
                        <TableCell sx={{ width: '200px' }}>評価者</TableCell>
                        <TableCell align="right" sx={{ width: '120px' }}>評価重み</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {evaluators.map((evaluator, index) => (
                        <TableRow key={index}>
                          <TableCell>{getCategoryLabel(evaluator.evaluation_category)}</TableCell>
                          <TableCell>{evaluator.evaluator_name || evaluator.evaluator_id}</TableCell>
                          <TableCell align="right">{formatNumber(evaluator.evaluation_weight * 100, 0)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* 営業指標（営業部門のみ） */}
          {employeeDeptType === 'sales' && salesMetrics && (
            <Card sx={{ mb: 3 }} variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  営業指標
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '150px' }}>指標</TableCell>
                        <TableCell align="right" sx={{ width: '150px' }}>目標</TableCell>
                        <TableCell align="right" sx={{ width: '150px' }}>実績</TableCell>
                        <TableCell align="right" sx={{ width: '120px' }}>達成率</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>期待受注額</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.target_expected_order)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.actual_expected_order)}</TableCell>
                        <TableCell align="right">
                          {calculateAchievementRate(salesMetrics.actual_expected_order, salesMetrics.target_expected_order)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>売上高</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.target_sales)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.actual_sales)}</TableCell>
                        <TableCell align="right">
                          {calculateAchievementRate(salesMetrics.actual_sales, salesMetrics.target_sales)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>受注粗利額</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.target_gross_profit)}</TableCell>
                        <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.actual_gross_profit)}</TableCell>
                        <TableCell align="right">
                          {calculateAchievementRate(salesMetrics.actual_gross_profit, salesMetrics.target_gross_profit)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* 技術部門の定量データ */}
          {employeeDeptType === 'tech' && evaluation.annual_sales !== null && (
            <Card sx={{ mb: 3 }} variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  技術部門定量データ
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">年間売上</Typography>
                    <Typography variant="body1">{formatCurrencyWithUnit(evaluation.annual_sales)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">評価時ランクコスト</Typography>
                    <Typography variant="body1">{formatCurrencyWithUnit(evaluation.current_rank_cost)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">チームメンバー数</Typography>
                    <Typography variant="body1">{evaluation.team_member_count || 0}名</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Dp値</Typography>
                    <Typography variant="body1">{formatNumber(evaluation.quantitative_dp_value, 4)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Dp評価点</Typography>
                    <Typography variant="body1">{formatNumber(evaluation.quantitative_dp_score)}点</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* 目標設定・達成基準 */}
          <GoalReferencePanel
            goal={goal}
            title="目標の申請状況"
            itemGoalsShown={hasAnyItemGoal(evaluationItems)}
          />

          {/* 評価項目詳細 */}
          {evaluationItems.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  評価項目詳細
                </Typography>
                {Object.keys(groupedItems)
                  .sort((a, b) => Number(a) - Number(b))
                  .map((categoryNum) => {
                    const category = groupedItems[Number(categoryNum)];
                    return (
                      <Box key={categoryNum} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                          評価観点{categoryNum}: {category.category_name}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: '80px' }}>項目No</TableCell>
                                <TableCell>評価項目</TableCell>
                                <TableCell align="center" sx={{ width: '80px' }}>重み</TableCell>
                                <TableCell align="center" sx={{ width: '100px' }}>自己評価</TableCell>
                                {managerEvaluators.map((evaluator) => (
                                  <TableCell key={evaluator.evaluator_id} align="center" sx={{ width: '160px' }}>
                                    上長評価<br />
                                    <Typography variant="caption" component="span">
                                      （{evaluator.evaluator_name} | {formatNumber(evaluator.evaluation_weight * 100, 0)}%）
                                    </Typography>
                                  </TableCell>
                                ))}
                                {deptHeadEvaluators.map((evaluator) => (
                                  <TableCell key={evaluator.evaluator_id} align="center" sx={{ width: '160px' }}>
                                    部門長評価<br />
                                    <Typography variant="caption" component="span">
                                      （{evaluator.evaluator_name} | {formatNumber(evaluator.evaluation_weight * 100, 0)}%）
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {category.items.map((item) => (
                                <TableRow key={item.item_detail_id || `${item.category_number}-${item.item_number}`}>
                                  <TableCell>{item.item_number}</TableCell>
                                  <TableCell>
                                    {item.item_description}
                                    <ItemGoalReference
                                      targetDescription={item.target_description}
                                      achievementCriteria={item.achievement_criteria}
                                      title="この項目の目標（確定）"
                                    />
                                  </TableCell>
                                  <TableCell align="center">{item.weight}</TableCell>
                                  <TableCell align="center">
                                    {formatNumber(item.first_evaluation_score, 0)}
                                  </TableCell>
                                  {managerEvaluators.map((evaluator) => (
                                    <TableCell key={evaluator.evaluator_id} align="center">
                                      {getEvaluatorScore(evaluator.evaluator_id, item.item_id || '')}
                                    </TableCell>
                                  ))}
                                  {deptHeadEvaluators.map((evaluator) => (
                                    <TableCell key={evaluator.evaluator_id} align="center">
                                      {getEvaluatorScore(evaluator.evaluator_id, item.item_id || '')}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {Number(categoryNum) < Object.keys(groupedItems).length && (
                          <Divider sx={{ my: 2 }} />
                        )}
                      </Box>
                    );
                  })}
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Typography color="text.secondary">
          この従業員の評価データはまだ作成されていません
        </Typography>
      )}
    </Box>
  );
};

export default EvaluationDetailView;
